import React, { useCallback, useState } from 'react';
import { IconAsChunk } from '@boschebike/ebds-react-icons';
import FooterScrollToTop from './FooterScrollToTop.js';
import FooterCopyrightNotice from './FooterCopyrightNotice.js';
import { useLegalLinks, linksIterator } from './private/footer-utils/links.js';
import ConsentPrompt from './ConsentPrompt.js';
import Button from './Button.js';
import VersionFooter from './VersionFooter.js';
const defaultButtons = [{
  url: 'https://www.bosch-ebike.com',
  Icon: () => /*#__PURE__*/React.createElement(IconAsChunk, {
    iconName: "BicycleE"
  }),
  label: 'Bosch eBike Systems',
  isExternal: true
}];
const defaultSocialLinks = [{
  url: 'https://www.facebook.com/boschebikesystems',
  Icon: () => /*#__PURE__*/React.createElement(IconAsChunk, {
    iconName: "Facebook"
  }),
  label: 'Facebook'
}, {
  url: 'https://www.youtube.com/user/boschebikesystems',
  Icon: () => /*#__PURE__*/React.createElement(IconAsChunk, {
    iconName: "Youtube"
  }),
  label: 'Youtube'
}, {
  url: 'https://www.instagram.com/boschebikesystems/',
  Icon: () => /*#__PURE__*/React.createElement(IconAsChunk, {
    iconName: "Instagram"
  }),
  label: 'Instagram'
}];
const defaultFooterLinks = [{
  label: 'Playbook',
  url: 'https://playbook.ebike.dev'
}, {
  label: 'GitHub',
  url: 'https://github.boschdevcloud.com'
}, {
  label: 'Digital platform enablers',
  url: 'https://platform.ebike.dev'
}];
const defaultLegalLinks = [{
  url: 'https://www.bosch-ebike.com/en/imprint',
  label: 'Corporate information'
}, {
  url: 'https://www.bosch.com/legal-notice/',
  label: 'Legal notice'
}, {
  url: 'https://www.bosch.com/data-protection-notice',
  label: 'Data protection notice (Privacy Policy)'
}];
const Footer = ({
  scrollToTop,
  legalLinks = defaultLegalLinks,
  buttons = defaultButtons,
  socialLinks = defaultSocialLinks,
  footerLinks = defaultFooterLinks,
  consentProps = {},
  withConsentPrompt = false,
  consentLang = 'en-GB',
  copyrightNotice = `© Robert Bosch GmbH ${new Date().getFullYear()}, all rights reserved`,
  claim = 'Invented for life',
  variant = Footer.variants.DARK,
  constrained = true,
  versionText = null,
  hideVersionFooter = true
}) => {
  const [consentRef, setConsentRef] = useState(null);
  const openConsent = useCallback(() => {
    if (!consentRef) {
      console.log('No consent DOM element found.');
      return;
    }
    const dock = consentRef?.querySelector('dock-privacy-settings');
    if (typeof dock.visible !== 'undefined') {
      dock.visible = true;
    }
  }, [consentRef]);
  const legalLinksElements = useLegalLinks(legalLinks, withConsentPrompt, consentLang, openConsent);
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("footer", {
    className: `ebds-container ebds-footer ebds-footer--${variant}`
  }, /*#__PURE__*/React.createElement("div", {
    className: `ebds-footer__content ebds-content ${constrained ? 'ebds-footer__content--constrained' : ''}`
  }, /*#__PURE__*/React.createElement("div", {
    className: "ebds-footer__content__main-row"
  }, /*#__PURE__*/React.createElement("div", {
    className: "ebds-footer__content__main-row__claim"
  }, claim), /*#__PURE__*/React.createElement("ul", {
    className: "ebds-footer__content__main-row__buttons"
  }, buttons.map(({
    url,
    Icon,
    label,
    onClick,
    isExternal
  }) => /*#__PURE__*/React.createElement("li", {
    key: url
  }, /*#__PURE__*/React.createElement(Button, {
    type: "button",
    mode: "tertiary",
    state: "normal",
    href: url,
    onClick: onClick,
    icon: /*#__PURE__*/React.createElement(Icon, null),
    text: label,
    srText: label,
    isExternal: isExternal,
    className: "ebds-footer-link"
  })))), /*#__PURE__*/React.createElement("ul", {
    className: "ebds-footer__content__main-row__links"
  }, footerLinks.map(linksIterator)), /*#__PURE__*/React.createElement("ul", {
    className: "ebds-footer__content__main-row__social"
  }, socialLinks.map(({
    url,
    Icon,
    label
  }) => /*#__PURE__*/React.createElement("li", {
    key: url
  }, /*#__PURE__*/React.createElement(Button, {
    type: "button",
    mode: "tertiary",
    state: "normal",
    href: url,
    icon: /*#__PURE__*/React.createElement(Icon, null),
    srText: label,
    isExternal: true,
    className: "ebds-footer-link"
  }))))), variant === Footer.variants.LIGHT && /*#__PURE__*/React.createElement("hr", null), /*#__PURE__*/React.createElement("div", {
    className: "ebds-footer__content__legal-row"
  }, /*#__PURE__*/React.createElement(FooterCopyrightNotice, {
    text: copyrightNotice
  }), /*#__PURE__*/React.createElement("ul", {
    className: "ebds-footer__content__legal-row__legal-menu"
  }, legalLinksElements), /*#__PURE__*/React.createElement(FooterScrollToTop, scrollToTop))), versionText && /*#__PURE__*/React.createElement(VersionFooter, {
    hide: hideVersionFooter
  }, /*#__PURE__*/React.createElement("span", null, "v", versionText))), withConsentPrompt ? /*#__PURE__*/React.createElement("div", {
    ref: setConsentRef
  }, /*#__PURE__*/React.createElement(ConsentPrompt, consentProps)) : null);
};
Footer.variants = {
  LIGHT: 'light',
  DARK: 'dark'
};
export default Footer;